import { action, computed, observable, runInAction } from 'mobx'
import {
  createSectionAssignmentConversationItem,
  getSectionAssignmentConversationItemsStream,
} from '../firestore/SectionAssignmentConversationItem'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SectionAssignmentConversationItem } from '../models/SectionAssignmentConversationItem'
import {
  PostAssignmentPromptType,
  PromptType,
  type StaticModelCollection,
} from '../types'
import { Cubit } from './core'
import { DateTime } from 'luxon'
import { SectionAssignmentConversationItemPromptTest } from '../models/SectionAssignmentConversationItemPromptTest'
import {
  createPromptTestConversationItem,
  getPromptTestConversationItems,
} from '../firestore/SectionAssignmentConversationItemPromptTest'

export type AssistantItemGroup<T extends AllowedConversationItemModels> = {
  id: string
  type: 'assistant'
  item: T
  rags: T[]
}
export type ImageGenItemGroup<T extends AllowedConversationItemModels> = {
  id: string
  type: 'image_gen'
  item: T
  imageHttpPath: string
}
export type UserItemGroup<T extends AllowedConversationItemModels> = {
  id: string
  type: 'user'
  item: T
}

export type RagItemGroup<T extends AllowedConversationItemModels> = {
  id: string
  type: 'rag'
  items: T[]
  eventCount: number
  filteredRoomIds: string[]
  filteredUserIds: string[]
}

export type ItemGroup<T extends AllowedConversationItemModels> =
  // | { type: 'noop' }
  | UserItemGroup<T>
  | RagItemGroup<T>
  | AssistantItemGroup<T>
  | ImageGenItemGroup<T>

type AllowedConversationItemModels =
  | SectionAssignmentConversationItem
  | SectionAssignmentConversationItemPromptTest

type AllowedCollectionType<T extends AllowedConversationItemModels> =
  StaticModelCollection<T>

type BaseClassArgs<T extends AllowedConversationItemModels> = {
  sectionId: string
  assignmentId: string
  conversationId: string
  modelConstructor: () => AllowedCollectionType<T>
}

export abstract class ConversationCubitBase<
  T extends AllowedConversationItemModels,
> extends Cubit {
  repository: FirebaseRepository
  sectionId: string
  assignmentId: string
  conversationId: string
  conversationItems: StaticModelCollection<T>

  @observable
  latestInitializedCreatedAt: DateTime

  @observable
  fetchCount: number = 0

  constructor(repository: FirebaseRepository, params: BaseClassArgs<T>) {
    super()
    this.repository = repository
    this.sectionId = params.sectionId
    this.conversationId = params.conversationId
    this.assignmentId = params.assignmentId
    this.latestInitializedCreatedAt = DateTime.now()
    this.conversationItems = params.modelConstructor()
  }

  abstract initialize(): void

  abstract addUserMessage(userMessage: string): Promise<string>

  @action
  setLatestInitializedCreatedAtIfUnset() {}

  @computed
  get conversationItemsSorted() {
    return this.conversationItems.models.sort((a, b) => {
      return (
        (a.data.createdAt?.getTime() || 0) - (b.data.createdAt?.getTime() || 0)
      )
    })
  }

  @computed
  get itemGroups(): ItemGroup<T>[] {
    const groups: ItemGroup<T>[] = []
    let rags: T[] = []
    let lastRagGroup: RagItemGroup<T> | undefined = undefined
    for (const item of this.conversationItemsSorted) {
      const role = item.data.role
      if (role === 'user') {
        const group: ItemGroup<T> = {
          id: item.id,
          type: 'user',
          item: item,
        }
        groups.push(group)
        lastRagGroup = undefined
      } else if (role === 'rag') {
        rags.push(item)
        const group: RagItemGroup<T> = lastRagGroup || {
          id: item.id,
          type: 'rag',
          items: [item],
          eventCount: 0,
          filteredRoomIds: [],
          filteredUserIds: [],
        }
        if (lastRagGroup) {
          lastRagGroup.items.push(item)
        } else {
          groups.push(group)
          lastRagGroup = group
        }

        group.eventCount = group.items.reduce(
          (acc, item) => acc + (item.data.eventIds?.length || 0),
          0
        )
        const filteredRoomIdsSet = group.items.reduce((acc, item) => {
          if (item.data.filteredRoomIds) {
            item.data.filteredRoomIds.forEach((id) => acc.add(id))
          }
          return acc
        }, new Set<string>())
        group.filteredRoomIds = Array.from(filteredRoomIdsSet)

        const filteredUserIdsSet = group.items.reduce((acc, item) => {
          if (item.data.filteredUserIds) {
            item.data.filteredUserIds.forEach((id) => acc.add(id))
          }
          return acc
        }, new Set<string>())
        group.filteredUserIds = Array.from(filteredUserIdsSet)
      } else if (role === 'assistant') {
        const group: ItemGroup<T> = {
          id: item.id,
          type: 'assistant',
          item: item,
          rags: rags,
        }
        rags = []
        groups.push(group)
        lastRagGroup = undefined
      } else if (role === 'image_gen') {
        if (item.data.imageHttpPath! && item.data.imageHttpPath !== '') {
          const group: ItemGroup<T> = {
            id: item.id,
            type: 'image_gen',
            item: item,
            imageHttpPath: item.data.imageHttpPath,
          }
          groups.push(group)
          lastRagGroup = undefined
        }
      }
    }

    return groups
  }

  @computed
  get shouldShowWorkingIndicator() {
    const models = this.conversationItemsSorted.filter(
      (item) => item.data.role === 'assistant' || item.data.role === 'user'
    )
    const lastItem = models[models.length - 1]
    if (
      lastItem &&
      lastItem.data.role === 'assistant' &&
      lastItem.data.complete === false
    ) {
      const createdAtDateTime = DateTime.fromJSDate(lastItem.data.createdAt)
      const diff = this.repository.currentMinute.diff(
        createdAtDateTime,
        'seconds'
      ).seconds
      return (
        diff < 60 &&
        (lastItem.data.userContent === undefined ||
          lastItem.data.userContent.trim() === '')
      )
    }
    if (lastItem && lastItem.data.role === 'user' && lastItem.data.createdAt) {
      const createdAtDateTime = DateTime.fromJSDate(lastItem.data.createdAt)
      const diff = this.repository.currentMinute.diff(
        createdAtDateTime,
        'seconds'
      ).seconds
      return diff <= 60
    }
    return false
  }

  @computed
  get requestTimedOut() {
    const models = this.conversationItems.models
      .filter(
        (item) => item.data.role === 'user' || item.data.role === 'assistant'
      )
      .sort((a, b) => {
        return (
          (a.data.createdAt?.getTime() || 0) -
          (b.data.createdAt?.getTime() || 0)
        )
      })
    const latestItem = models[models.length - 1]
    if (
      latestItem &&
      latestItem.data.role === 'user' &&
      latestItem.data.createdAt
    ) {
      const createdAtDateTime = DateTime.fromJSDate(latestItem.data.createdAt)
      const diff = this.repository.currentMinute.diff(
        createdAtDateTime,
        'seconds'
      ).seconds
      return diff > 80
    }
    return false
  }

  @computed
  get canSend() {
    // 1. there are 0 messages
    if (this.conversationItems.models.length === 0) return true

    const allItems = this.conversationItemsSorted

    // 2. last message is an assistant message and it is complete
    const lastItem = allItems[allItems.length - 1]
    if (
      lastItem &&
      (lastItem.data.role === 'assistant' ||
        lastItem.data.role === 'image_gen') &&
      lastItem.data.complete === true
    ) {
      return true
    }

    // 3. last user message is over 60 seconds old
    const lastUserItem = this.conversationItemsSorted.filter(
      (item) => item.data.role === 'user'
    )
    const lastUserMessage = lastUserItem[lastUserItem.length - 1]
    if (lastUserMessage && lastUserMessage.data.createdAt) {
      const createdAtDateTime = DateTime.fromJSDate(
        lastUserMessage.data.createdAt
      )
      const diff = this.repository.currentMinute.diff(
        createdAtDateTime,
        'seconds'
      ).seconds
      return diff > 80
    }

    return false
  }
}

export class InstructorAssignmentConversationCubit extends ConversationCubitBase<SectionAssignmentConversationItem> {
  constructor(
    repository: FirebaseRepository,
    params: Omit<
      BaseClassArgs<SectionAssignmentConversationItem>,
      'modelConstructor'
    >
  ) {
    super(repository, {
      ...params,
      modelConstructor: () =>
        SectionAssignmentConversationItem.emptyCollection(repository),
    })
  }

  initialize(): void {
    this.addStream(
      getSectionAssignmentConversationItemsStream(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        conversationId: this.conversationId,
      }),
      (conversationItems) => {
        this.conversationItems.replaceModels(conversationItems)
        // this.setLatestInitializedCreatedAtIfUnset()
        runInAction(() => {
          this.fetchCount += 1
        })
      },
      { name: 'assignment-conversation-items' }
    )
  }

  addUserMessage(userMessage: string) {
    return createSectionAssignmentConversationItem(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      conversationId: this.conversationId,
      userId: this.repository.uid,
      role: 'user',
      userContent: userMessage,
    })
  }
}

export class PromptTestConversationCubit extends ConversationCubitBase<SectionAssignmentConversationItemPromptTest> {
  promptVersionId: string

  constructor(
    repository: FirebaseRepository,
    params: Omit<
      BaseClassArgs<SectionAssignmentConversationItemPromptTest>,
      'modelConstructor'
    > & { promptVersionId: string }
  ) {
    super(repository, {
      ...params,
      modelConstructor: () =>
        SectionAssignmentConversationItemPromptTest.emptyCollection(repository),
    })
    this.promptVersionId = params.promptVersionId
  }

  initialize(): void {
    this.addStream(
      getPromptTestConversationItems(this.repository, {
        promptType: PromptType.postAssignment,
        subType: PostAssignmentPromptType.transcriptSearch,
        conversationId: this.conversationId,
        promptVersionId: this.promptVersionId,
      }),
      (conversationItems) => {
        this.conversationItems.replaceModels(conversationItems)
        // this.setLatestInitializedCreatedAtIfUnset()
        runInAction(() => {
          this.fetchCount += 1
        })
      },
      { name: 'assignment-conversation-items' }
    )
  }

  addUserMessage(userMessage: string) {
    return createPromptTestConversationItem(this.repository, {
      promptType: PromptType.postAssignment,
      subType: PostAssignmentPromptType.transcriptSearch,
      promptVersionId: this.promptVersionId,
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      conversationId: this.conversationId,
      userId: this.repository.uid,
      role: 'user',
      userContent: userMessage,
    })
  }
}
